"use client";
import React, { useEffect, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { useRouter } from "next/navigation";
import { AuthType, RecoverPasswordProps, TypeProp } from "@/interface/authentication";
import toast from "react-hot-toast";
import axios from "axios";
import { useSelector, useDispatch } from "react-redux";
import { clearUserInfo, updateRecoverPassword } from "@/store/slices/userSlice";
import { AppState, AppDispatch } from "@/store/configureStore";
import {
	RESEND_OTP_WITH_EMAIL_API,
	RESEND_OTP_WITH_NUMBER_API,
	RECOVER_PASSWORD_WITH_NUMBER_API,
	RECOVER_PASSWORD_WITH_EMAIL_API,
	VERIFY_OTP_API,
} from "@/constants";
import { updateIdentifier } from "@/store/slices/identifier";

interface Props {
	setIsEnterOtp?: React.Dispatch<React.SetStateAction<boolean>>;
	type?: AuthType;
}

const useRecoverPassword = ({ setIsEnterOtp, type }: Props) => {
	const user = useSelector((state: AppState) => state.user);
	const dispatch = useDispatch<AppDispatch>();
	const router = useRouter();
	const [tokens, setTokens] = useState(["", "", "", "", "", ""]);
	const inputRefs = useRef<any[]>([]);
	const token = tokens.join("");
	const identifier = useSelector((state: AppState) => state.identifier);

	const otpSchema = yup.object({
		email:
			type === "email"
				? yup
						.string()
						.email("Invalid email address")
						.required("Email is required")
				: yup.string(),
		phone_number:
			type === "phone_number"
				? yup.string().required("Phone number is required")
				: yup.string(),
	});

	const {
		register,
		handleSubmit,
		formState: { errors, isSubmitting },
		reset,
		resetField,
	} = useForm({
		resolver: yupResolver(otpSchema),
	});
	console.log(identifier);

	const submitForm = async (data: RecoverPasswordProps) => {
		const endpoint =
			type === AuthType.EMAIL
				? RECOVER_PASSWORD_WITH_EMAIL_API
				: RECOVER_PASSWORD_WITH_NUMBER_API;
		try {
			const response = await axios.post(endpoint, {
				email: type === AuthType.EMAIL && data.email,
				phoneNumber:
					type === AuthType.PHONE_NUMBER &&
					(data?.phone_number?.startsWith("0")
						? `+234${data?.phone_number?.slice(1)}`
						: `+234${data?.phone_number}`),
			});
			// console.log("API Response", response?.data);
			dispatch(clearUserInfo());
			dispatch(
				updateRecoverPassword({
					id: response?.data?.user,
				})
			);
			dispatch(
				updateIdentifier({
					identifier:
						type === AuthType.EMAIL
							? data?.email
							: data?.phone_number?.startsWith("0")
							? `+234${data?.phone_number?.slice(1)}`
							: `+234${data?.phone_number}`,
				})
			);
			router.push("/verify-token");
		} catch (error: string | any) {
			// console.log("API Error", error);
			toast.remove();
			toast.error(`An error occured: ${error?.response?.data?.error}. Try again!`);
		}
	};

	const handleResendOTP = async () => {
		const endpoint = identifier?.identifier.startsWith("+234")
			? RESEND_OTP_WITH_NUMBER_API
			: RESEND_OTP_WITH_EMAIL_API;
		try {
			const response = await axios.post(endpoint, {
				email: identifier?.identifier.startsWith("+234")
					? ""
					: identifier?.identifier,
				phoneNumber: identifier?.identifier.startsWith("+234")
					? identifier?.identifier
					: "",
			});
			// console.log("API Response", response);
			toast.remove();
			toast.success(`${response.data.msg}`);
		} catch (error: string | any) {
			// console.log("API Error", error);
			toast.error(
				`An error occured trying resend OTP: ${error?.response?.data.error}`
			);
		}
	};

	useEffect(() => {
		resetField(type === AuthType.EMAIL ? AuthType.PHONE_NUMBER : AuthType.EMAIL);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [type]);

	useEffect(() => {
		inputRefs.current[0]?.focus();
	}, []);

	// a function that handles the input change and moves the focus to the next input field
	const handleInputChange = (index: number, e: React.ChangeEvent<HTMLInputElement>) => {
		const value = e.target.value;
		if (value.length === 1 && index < inputRefs.current.length - 1) {
			inputRefs.current[index + 1].focus();
		}
		const updatedTokens = [...tokens];
		updatedTokens[index] = value;
		setTokens(updatedTokens);
	};

	const submitToken = async (e: React.FormEvent) => {
		e.preventDefault();
		try {
			const response = await axios.post(VERIFY_OTP_API, {
				identifier: identifier?.identifier,
				otp: token,
			});
			toast.success(response?.data?.user?.msg);
			// console.log("API Response", response?.data, token, identifier);
			router.push("/new-password");
		} catch (error: string | any) {
			// console.log("API Error", error);
			toast.error(`An error occured: ${error?.response?.data.error}. Try again!`);
		}
	};

	const handleKeyDown = (index: number, e: React.KeyboardEvent<HTMLInputElement>) => {
		if (
			e.key === "Backspace" &&
			index > 0 &&
			tokens[index] === "" &&
			inputRefs.current[index - 1]
		) {
			inputRefs.current[index - 1].focus();
		}
	};

	const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
		e.preventDefault();
		const pastedData = e.clipboardData.getData("text/plain").slice(0, 6);
		const newOtp = pastedData.split("").slice(0, 6);
		// console.log(pastedData, newOtp);
		setTokens(newOtp);
		if (inputRefs.current[5]) {
			inputRefs.current[5].focus();
		}
	};

	return {
		register,
		handleSubmit,
		errors,
		submitForm,
		handleInputChange,
		handleKeyDown,
		handlePaste,
		inputRefs,
		tokens,
		submitToken,
		handleResendOTP,
		isSubmitting,
	};
};

export default useRecoverPassword;
